import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';
import { JwtToken } from 'types/dashboard.d';

import { LogoutOnUserInactivity } from './LogoutOnUserInactivity';
import { LoggedOutRouter } from 'borrower/routes/LoggedOutRouter';
import { BorrowerRouter } from 'borrower/routes/Router';
import { CapitalProviderRouter } from 'capitalProvider/routes/Router';
import { useLoginRedirectPathContext } from 'packages/logged-out/hooks/LoginRedirectPathProvider';
import { useAuthToken } from 'packages/logged-out/hooks/useAuthManagement';
import { ValleyBankRouter } from 'source/valleyBank/routes/Router';

/**
 * All apollo requests must have a valid JWT token.
 * Calling the `getUser` query at the same level as the `LoggedOutRouter` will cause an unauthenticated request to be made,
 * throwing a 400 error every time the login page loads.
 *
 * Any router that requires user fetch should live in this `LoggedInRouter` to guarantee that the user is authenticated.
 */
const LoggedInRouter = ({ decodedToken }: { decodedToken: JwtToken }) => {
  const [, , removeAuthToken] = useAuthToken();

  const { data: userData } = useGetUserQuery();

  useEffect(() => {
    if (userData?.user) {
      Sentry.setUser({
        id: userData.user.id,
        organization: userData.user.organization.slug,
      });
    }
  }, [userData]);

  if (!userData || !userData.user) {
    return <></>;
  }

  if (decodedToken.userType === 'SUPER_USER') {
    removeAuthToken();
    window.location.replace('/');
  }

  if (decodedToken.userType === 'BORROWER') {
    return <BorrowerRouter userData={userData} />;
  }

  if (decodedToken.userType === 'CAPITAL_PROVIDER') {
    if (userData.user.organization.slug === 'valleyBank') {
      return (
        <LogoutOnUserInactivity>
          <ValleyBankRouter />;
        </LogoutOnUserInactivity>
      );
    }

    return <CapitalProviderRouter userData={userData} />;
  }
  throw new Error('Could not find correct logged in route');
};

export const Router = () => {
  const location = useLocation();

  const { redirectPath, setRedirectPath } = useLoginRedirectPathContext();

  const [authToken, , , getDecodedToken] = useAuthToken();
  const decodedToken = getDecodedToken();

  useEffect(() => {
    if (!authToken && redirectPath === null) {
      setRedirectPath(location.pathname + location.search);
    }
  }, [authToken, redirectPath, location, setRedirectPath]);

  if (!authToken) {
    return <LoggedOutRouter />;
  }

  if (decodedToken) {
    return <LoggedInRouter decodedToken={decodedToken} />;
  }

  throw new Error('Could not find correct route');
};
